import React, { useState, useEffect } from 'react';
import CommonDataTable from '../../Common/CommonDataTable';
import LoadingSpinner from '../../Common/LoadingSpinner';

const GRNAllocationGrid = ({
  supplier,
  grnAllocations = [],
  onGRNAllocationsChange,
  errors = {},
  loading = false,
  onLoadAvailableGRNs
}) => {
  const [availableGRNs, setAvailableGRNs] = useState([]);
  const [loadingGRNs, setLoadingGRNs] = useState(false);
  const [showAddGRN, setShowAddGRN] = useState(false);

  // Load available GRNs when supplier changes
  useEffect(() => {
    if (supplier && onLoadAvailableGRNs) {
      loadAvailableGRNs();
    }
  }, [supplier]);

  const loadAvailableGRNs = async () => {
    if (!supplier || !onLoadAvailableGRNs) return;

    setLoadingGRNs(true);
    try {
      const grns = await onLoadAvailableGRNs(supplier);
      setAvailableGRNs(grns);
    } catch (error) {
      console.error('Error loading available GRNs:', error);
    } finally {
      setLoadingGRNs(false);
    }
  };

  const handleAmountChange = (index, field, value) => {
    const updatedAllocations = [...grnAllocations];
    updatedAllocations[index] = {
      ...updatedAllocations[index],
      [field]: parseFloat(value) || 0
    };
    // Calculate remaining balance
    if (field === 'amountPaid') {
      updatedAllocations[index].remainingBalance =
        updatedAllocations[index].outstandingBalance - (parseFloat(value) || 0);
    }
    onGRNAllocationsChange(updatedAllocations);
  };

  const handleNotesChange = (index, notes) => {
    const updatedAllocations = [...grnAllocations];
    updatedAllocations[index] = {
      ...updatedAllocations[index],
      notes
    };
    onGRNAllocationsChange(updatedAllocations);
  };

  const handleRemoveGRN = (index) => {
    const updatedAllocations = grnAllocations.filter((_, i) => i !== index);
    onGRNAllocationsChange(updatedAllocations);
  };

  const handleAddGRN = (grn) => {
    const newAllocation = {
      grn: grn._id,
      grnNo: grn.grnNo,
      grnDate: grn.grnDate,
      dueDate: grn.dueDate,
      grnAmount: grn.grnAmount,
      outstandingBalance: grn.outstandingBalance,
      amountPaid: 0,
      remainingBalance: grn.outstandingBalance,
      notes: ''
    };

    onGRNAllocationsChange([...grnAllocations, newAllocation]);
    setShowAddGRN(false);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const columns = [
    {
      key: 'grnNo',
      header: 'GRN No.',
      width: '120px'
    },
    {
      key: 'grnDate',
      header: 'GRN Date',
      width: '100px',
      render: (value) => formatDate(value)
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      width: '100px',
      render: (value) => formatDate(value)
    },
    {
      key: 'grnAmount',
      header: 'GRN Amount',
      width: '120px',
      render: (value) => parseFloat(value).toFixed(2)
    },
    {
      key: 'outstandingBalance',
      header: 'Outstanding Balance',
      width: '140px',
      render: (value) => parseFloat(value).toFixed(2)
    },
    {
      key: 'amountPaid',
      header: 'Amount Paid',
      width: '140px',
      render: (value, row, index) => (
        <input
          type="number"
          value={value === 0 ? '0' : (value || '')}
          onChange={(e) => handleAmountChange(index, 'amountPaid', e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          min="0"
          max={row.outstandingBalance}
          step="0.01"
        />
      )
    },
    {
      key: 'remainingBalance',
      header: 'Remaining Balance',
      width: '140px',
      render: (value) => (
        <span className={`font-medium ${value > 0 ? 'text-orange-600' : 'text-green-600'}`}>
          {parseFloat(value).toFixed(2)}
        </span>
      )
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (value, row, index) => (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleNotesChange(index, e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Add notes..."
        />
      )
    }
  ];

  const availableGRNColumns = [
    {
      key: 'grnNo',
      header: 'GRN No.',
      width: '120px'
    },
    {
      key: 'grnDate',
      header: 'GRN Date',
      width: '100px',
      render: (value) => formatDate(value)
    },
    {
      key: 'grnAmount',
      header: 'GRN Amount',
      width: '120px',
      render: (value) => parseFloat(value).toFixed(2)
    },
    {
      key: 'outstandingBalance',
      header: 'Outstanding Balance',
      width: '140px',
      render: (value) => parseFloat(value).toFixed(2)
    }
  ];

  // Filter out already selected GRNs
  const filteredAvailableGRNs = availableGRNs.filter(grn =>
    !grnAllocations.some(allocation => allocation.grn === grn._id)
  );

  const totalAmountPaid = grnAllocations.reduce((sum, allocation) => sum + (allocation.amountPaid || 0), 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
        <span className="text-xs font-semibold text-white">GRN Allocation</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-indigo-100">Total: {parseFloat(totalAmountPaid).toFixed(2)}</span>
          {supplier && (
            <button
              onClick={() => setShowAddGRN(true)}
              className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
              disabled={loadingGRNs}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add GRN
            </button>
          )}
        </div>
      </div>
      <div className="p-4">

      {errors.grnAllocations && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {errors.grnAllocations}
          </p>
        </div>
      )}

      {!supplier ? (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-lg font-medium">Select a supplier first</p>
          <p className="text-sm">Choose a supplier to load available GRNs for payment</p>
        </div>
      ) : grnAllocations.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-lg font-medium">No GRNs allocated</p>
          <p className="text-sm">Click "Add GRN" to start allocating payments</p>
        </div>
      ) : (
        <CommonDataTable
          data={grnAllocations}
          columns={columns}
          loading={loading}
          emptyMessage="No GRN allocations"
          customActions={[
            {
              label: 'Remove',
              onClick: (row, index) => handleRemoveGRN(index),
              icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              ),
              color: 'text-red-600 hover:text-red-800'
            }
          ]}
        />
      )}

      {/* Add GRN Modal */}
      {showAddGRN && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-xl px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Add Available GRNs
                  </h3>
                </div>
                <button
                  onClick={() => setShowAddGRN(false)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingGRNs ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                  <span className="ml-2 text-gray-600">Loading available GRNs...</span>
                </div>
              ) : filteredAvailableGRNs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium">No available GRNs</p>
                  <p className="text-sm">All GRNs for this supplier are already allocated or paid</p>
                </div>
              ) : (
                <CommonDataTable
                  data={filteredAvailableGRNs}
                  columns={availableGRNColumns}
                  emptyMessage="No available GRNs"
                  onRowClick={handleAddGRN}
                  customActions={[
                    {
                      label: 'Add',
                      onClick: handleAddGRN,
                      icon: (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      ),
                      color: 'text-green-600 hover:text-green-800'
                    }
                  ]}
                />
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default GRNAllocationGrid;